import Vue from 'vue'
import Vuex from 'vuex'

Vue.use(Vuex)

import checkout from './modules/checkout.js'

export default new Vuex.Store({
  state: {
    nav: {
      open: false
    },
    purchase: {
      modalOpen: false,
      // Which bundle type the modal opened for ('credits' | 'gold') — the
      // actual bundles for sale live in the checkout module, fetched from
      // Stripe (GET /api/checkout/prices), not hardcoded here.
      modalItems: 'credits',
    },
    user: {
      token: null,
      id: null,
      email: null,
      avatar: null,
      username: null,
    },
    dbInfo: {
      credits: null,
      gold: null,
    },
    guilds: {
      list: [],
      lastUpdated: null,
    },
    stats: {
      mostPlayed: [],
      totalWins: 0,
      totalGames: 0,
      winrate: 0,
    }
  },
  getters: {
    getToken: state => {
      let token = state.user.token
      if(!token) {
      // Fallback only, not reactive: document.cookie isn't a Vuex-tracked
      // dependency, so this branch won't trigger getter re-evaluation on its own.
      let row = document.cookie.split('; ').find(row => row.startsWith('token'))
        if(row) {
          token = row.split('=')[1]
        }
      }
      return token
    },
    getUser: state => state.user,
    getdbInfo: state => state.dbInfo,
    getGuilds: state => state.guilds,
    getStats: state => state.stats,
  },
  mutations: {
    setToken(state, token) {
      state.user.token = token
    },
    setUser(state, user) {
      state.user.id = user.id
      // Discord users with no custom avatar have `avatar: null` — fall back to their
      // default avatar (indexed off the user ID under Discord's post-2023 numbering).
      state.user.avatar = user.avatar
        ? `https://cdn.discordapp.com/avatars/${user.id}/${user.avatar}.png?size=64`
        : `https://cdn.discordapp.com/embed/avatars/${Number((BigInt(user.id) >> 22n) % 6n)}.png`
      state.user.username = user.global_name || user.username
    },
    setDBInfo(state, dbInfo) {
      state.dbInfo = { ...dbInfo }
      // Add helper fields
      state.dbInfo.credits = dbInfo.balance || 0
      state.dbInfo.gold = dbInfo.goldBalance || 0
    },
    setStats(state, stats) {
      state.stats = { ...stats }
    },
    togglePurchaseModal(state) {
      state.purchase.modalOpen = !state.purchase.modalOpen
    },
    setPurchaseModalOpen(state, open) {
      state.purchase.modalOpen = open
    },
    setModalItems(state, modalItems) {
      state.purchase.modalItems = modalItems
    },
    setGuilds(state, guilds) {
      state.guilds.lastUpdated = Date.now()
      state.guilds.list = guilds
    }
  },
  actions: {
    openPurchaseModal({ commit }, { type = 'credits' } = {}) {
      commit('setModalItems', type)
      commit('setPurchaseModalOpen', true)
    },
    login(context, token) {
      context.commit('setToken', token)
    },
    logout(context) {
      document.cookie = 'token=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
      context.commit('setToken', null)
      window.location.reload()
    },
    async fetchUserInfo(context, force) {
      let userData;
      if(!force) {
        // Retrieve user data from local storage
        userData = JSON.parse(localStorage.getItem('user'))
        if(userData && userData.date < Date.now() - 1000 * 60 * 60 * 24) {
          // If user data is more than than 24 hours old, fetch new data
          userData = undefined
        }
      }

      // If we don't have user data, or we're forcing a refresh, fetch it from the API
      if(this.getters.getToken && !userData) {
          let res = await fetch('https://discord.com/api/users/@me', {
              headers: {
                  authorization: `Bearer ${this.getters.getToken}`
              }
          }).catch(console.error)
          
          userData = await res.json()
          userData.date = Date.now()

          // Save the user data to localStorage
          localStorage.setItem('user', JSON.stringify(userData))
      }
      // Update user
      context.commit('setUser', userData)
    },
    async fetchDBInfo(context) {
      if(this.getters.getToken && this.getters.getUser) {
        let res = await fetch('/api/userInfo?userID=' + this.getters.getUser.id, {
          method: 'GET',
          headers: {
              authorization: `Bearer ${this.getters.getToken}`
          }
        }).catch(console.error)
        
        let response = await res.json()
        // Update user
        context.commit('setDBInfo', response)
      }
    },
    async fetchStats(context) {
      if(this.getters.getToken && this.getters.getUser) {
        let res = await fetch('/api/userStats?userID=' + this.getters.getUser.id, {
          method: 'GET',
          headers: {
              authorization: `Bearer ${this.getters.getToken}`
          }
        }).catch(console.error)
        
        let response = await res.json()
        // Update user
        context.commit('setStats', response)
      }
    },
    async fetchAllUserInfo({ dispatch }) {
      await dispatch('fetchUserInfo', false)
      await dispatch('fetchDBInfo')
      await dispatch('fetchStats')
    },
  },
  modules: {
    checkout
  }
})
